// src/components/Signup.js

import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import UserPool from '../../UserPool';
import api, { consoleDevLog } from "../../apiConfig";
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import { CognitoUser, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

const Signup = () => {
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [brokerageInfo, setBrokerageInfo] = useState('');
  const [brokerageAddress, setBrokerageAddress] = useState('');
  const [brokerageLogo, setBrokerageLogo] = useState(null);
  const [brokerageLogoUrl, setBrokerageLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [signupStatus, setSignupStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Basic validation
    if (!file.type.startsWith('image/')) {
      setSignupStatus({ success: false, message: 'Please upload an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setSignupStatus({ success: false, message: 'Image must be less than 5MB' });
      return;
    }

    setUploadingLogo(true);
    try {
      // Create a FormData object
      const formData = new FormData();
      formData.append('file', file);

      // Upload using apiConfig
      const response = await api.post('/images/upload-brokerage-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      setBrokerageLogoUrl(response.data.url);
      setBrokerageLogo(file);
      setSignupStatus({ success: true, message: 'Logo uploaded successfully' });
    } catch (error) {
      console.error('Logo upload error:', error);
      setSignupStatus({ 
        success: false, 
        message: error.response?.data?.detail || error.message || 'Failed to upload logo' 
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    
    if (password !== confirmPassword) {
      setSignupStatus({ success: false, message: 'Passwords do not match' });
      return;
    }

    // Validate phone number format (basic validation)
    const phoneRegex = /^\+?[\d\s-]+$/;
    if (!phoneRegex.test(phoneNumber)) {
      setSignupStatus({ success: false, message: 'Please enter a valid phone number' });
      return;
    }

    // Create attribute list for Cognito with custom attributes
    const attributeList = [
      new CognitoUserAttribute({
        Name: 'custom:firstName',
        Value: firstName
      }),
      new CognitoUserAttribute({
        Name: 'custom:lastName',
        Value: lastName
      }),
      new CognitoUserAttribute({
        Name: 'custom:phoneNumber',
        Value: phoneNumber
      }),
      new CognitoUserAttribute({
        Name: 'custom:brokerageInfo',
        Value: brokerageInfo
      }),
      new CognitoUserAttribute({
        Name: 'custom:brokerageAddr',
        Value: brokerageAddress
      })
    ];

    // Add logo URL if one was uploaded
    if (brokerageLogoUrl) {
      attributeList.push(new CognitoUserAttribute({
        Name: 'custom:brokerageLogo',
        Value: brokerageLogoUrl
      }));
    }

    setLoading(true);
    UserPool.signUp(email, password, attributeList, null, (err, data) => {
      if (err) {
        console.error('Signup error', err);
        setSignupStatus({ success: false, message: err.message || JSON.stringify(err) });
      } else {
        consoleDevLog('Signup success', data);
        setSignupStatus({ 
          success: true, 
          message: 'Please enter the verification code sent to your email.' 
        });
        setShowVerification(true);
        // Store the CognitoUser object for verification
        const user = new CognitoUser({
          Username: email,
          Pool: UserPool,
        });
        setCognitoUser(user);
      }
      setLoading(false);
    });
  };

  const handleVerification = (event) => {
    event.preventDefault();
    if (!cognitoUser) return;

    setLoading(true);
    cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
      if (err) {
        console.error('Verification error', err);
        setSignupStatus({ success: false, message: err.message || JSON.stringify(err) });
      } else {
        consoleDevLog('Verification success', result);
        setSignupStatus({ 
          success: true, 
          message: 'Account verified successfully! You can now log in.' 
        });
        // Redirect to login after a short delay
        setTimeout(() => {
          history.push('/login');
        }, 2000);
      }
      setLoading(false);
    });
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh', backgroundColor: '#f5f5f5', paddingTop: '100px' }}>
      <div style={{ width: '300px', padding: '20px', border: '1px solid #ddd', backgroundColor: '#fff', borderRadius: '5px', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)' }}>
        <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Create Account</h2>
        {!showVerification ? (
          <form onSubmit={onSubmit}>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="tel"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="Brokerage Name"
                value={brokerageInfo}
                onChange={(e) => setBrokerageInfo(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="Brokerage Address"
                value={brokerageAddress}
                onChange={(e) => setBrokerageAddress(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
              />
            </div>

            {/* Brokerage Logo Upload */}
            <div style={{ marginBottom: '20px' }}>
              <input
                type="file"
                accept="image/*"
                id="brokerage-logo"
                style={{ display: 'none' }}
                onChange={handleLogoUpload}
              />
              <label htmlFor="brokerage-logo">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  style={{ width: '100%' }}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? 'Uploading...' : 'Upload Brokerage Logo'}
                </Button>
              </label>
              {brokerageLogo && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <img 
                    src={URL.createObjectURL(brokerageLogo)} 
                    alt="Brokerage Logo Preview" 
                    style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain' }}
                  />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '10px' }}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              style={{ marginBottom: '15px' }}
            >
              {loading ? <CircularProgress size={24} style={{ color: '#fff' }} /> : 'Sign Up'}
            </Button>
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <Link to="/login" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Already have an account? Log in
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerification}>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="Enter 6-digit verification code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                required
                maxLength="6"
                pattern="[0-9]{6}"
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              style={{ marginBottom: '15px' }}
            >
              {loading ? <CircularProgress size={24} style={{ color: '#fff' }} /> : 'Verify Code'}
            </Button>
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <Button
                variant="text"
                color="primary"
                onClick={() => {
                  if (cognitoUser) {
                    cognitoUser.resendConfirmationCode((err) => {
                      if (err) {
                        setSignupStatus({ success: false, message: err.message });
                      } else {
                        setSignupStatus({ success: true, message: 'New verification code sent!' });
                      }
                    });
                  }
                }}
              >
                Resend Code
              </Button>
            </div>
          </form>
        )}
        {signupStatus && (
          <div style={{ 
            color: signupStatus.success ? 'green' : 'red', 
            marginTop: '10px', 
            textAlign: 'center',
            padding: '10px',
            backgroundColor: signupStatus.success ? '#e8f5e9' : '#ffebee',
            borderRadius: '5px'
          }}>
            {signupStatus.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
